import React, { useMemo, useState, useEffect, useRef } from "react"
import {
  Card,
  CardBody,
  Col,
  Container,
  Row,
  UncontrolledTooltip,
  UncontrolledDropdown,
  DropdownToggle,
  DropdownMenu,
  DropdownItem,
  Button,
  InputGroup
} from "reactstrap"
import Breadcrumbs from "components/Common/Breadcrumb"
import { useNavigate } from "react-router-dom"
import TableContainerV1 from "components/Common/TableContainerV1"
import { getCustomerQueryList } from "helpers/UAM/inquiry.helper"
import moment from "moment"
import Flatpickr from "react-flatpickr";

const ListQuery = () => {
  document.title="Query | DMS"
  const navigate = useNavigate();

  const [queryList, setQueryList] = useState([]);
  const [pagination, setPagination] = useState({
    pageIndex: 0,
    pageCount: 0,
    totalRecords: 0,
  });
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [searchValue, setSearchValue] = useState("");
  const [columnId, setColumnId] = useState("id");
  const [sortingOrderStatus, setSortingOrderStatus] = useState("desc");
  const [filters, setFilters] = useState({
    startDate: '',
    endDate: '',
    status: ''
  });
  const flatpickrRef = useRef(null);

  const [sortingOptions, setSortingOptions] = useState({
    column: "id",
    isAsc: false,
    optionalColumn: ["Action"],
  });


  const getQuery = async (bodyData) => {
    const response = await getCustomerQueryList(bodyData);
    if (response.status) {
      setQueryList(response.data.data);
      const { totalItems, totalPages, currentPage } = response.data
      setPagination({
        pageIndex: currentPage,
        pageCount: totalPages,
        totalRecords: totalItems,
      })
    } else {
      setQueryList([])
      setPagination({
        pageIndex: 0,
        pageCount: 0,
        totalRecords: 0,
      })
    }
  }

  useEffect(() => {
    const bodyData = {
      filterType: filters,
      page: currentPage,
      searchKey: searchValue,
      limit: pageSize,
      columnName: columnId,
      sortingOrder: sortingOrderStatus,
    }
    getQuery(bodyData);
  }, [searchValue, pageSize, columnId, sortingOrderStatus, currentPage]);


  const handlePageChange = (page, pageSize) => {
    setPageSize(pageSize);
    setCurrentPage(page);
  }

  const handleSortColumn = (option) => {
    setSortingOptions(option);
    const columnName = option.column;
    setColumnId(columnName);
    const sortingOrder = option.isAsc ? "asc" : "desc";
    setSortingOrderStatus(sortingOrder);
  };


  const handleSearchFilter = (value) => {
    setCurrentPage(1)
    setSearchValue(value);
  };
  const handleClearSearch = () => {
    setSearchValue("");
    setFilters({
      startDate: '',
      endDate: '',
      status: ''
    })
    flatpickrRef.current.flatpickr.clear();
    let bodyData = {
      filterType: {
        startDate: '',
        endDate: '',
        status: ''
      },
      page: currentPage,
      searchKey: "",
      limit: pageSize,
      columnName: columnId,
      sortingOrder: sortingOrderStatus,
    }

    getQuery(bodyData);
  };


  const ProductDescription = ({ value }) => {
    if (value && value.length > 50) {
      let newValue = value.substring(0, 50) + '...';
      return (
        <span className="d-inline" tabIndex="0" data-toggle="tooltip" title={value} dir="top" data-placement="top">
         {newValue}
        </span>
      )
    }
    return (
      <span>{value}</span>
    )
  }

  const getStatusComponent = (val) => {
    const statusMap = {
      resolve: <p className="bg-green rounded-pill status-pill text-white  d-flex justify-content-center p-1">Resolved</p>,
      lost: <p className="bg-danger rounded-pill status-pill text-white  d-flex justify-content-center p-1">Closed (Lost)</p>,
      open: <p className="bg-warning rounded-pill status-pill text-white  d-flex justify-content-center p-1" >Open</p>,
    };

    return statusMap[val] || null;
  };
  
  const columns = useMemo(
    () => [
      {
        Header: "ID",
        accessor: "id",
        Cell: cellProps => {
          return cellProps.value;
        },
      },
      {
        Header: "Query",
        accessor: "description",
        width: "100px",
        filterable: true,
        Cell: cellProps => {
          return <ProductDescription value={cellProps.value} />
        },
      },
      {
        Header: "Status",
        accessor: "queryStatus",
        filterable: true,
        Cell: (cellProps) => getStatusComponent(cellProps?.value)
      },
      {
        Header: "Created At",
        accessor: "createdAt",
        filterable: true,
        Cell: cellProps => {
          return cellProps.value ? moment(cellProps.value).format("MMM DD, YYYY HH:mm A") : ""
        },
      },
      {
        Header: "Action",
        Cell: cellProps => {
          return (
            <UncontrolledDropdown>
              <DropdownToggle tag="a" className="card-drop">
                <i className="mdi mdi-dots-horizontal font-size-18"></i>
              </DropdownToggle>

              <DropdownMenu className="dropdown-menu-end">
                <DropdownItem
                  onClick={() => {
                    const edit = cellProps.row.original
                    handleView(edit)
                  }}
                >
                  <i
                    className="mdi mdi-eye font-size-16 text-success me-1"
                    id="edittooltip"
                  ></i>
                  View
                  <UncontrolledTooltip placement="top" target="edittooltip">
                    View
                  </UncontrolledTooltip>
                </DropdownItem>
              </DropdownMenu>
            </UncontrolledDropdown>
          )
        },
      },
    ],
    []
  )


  const handleView = arg => {
    const id = arg.id;
    const enCodedId = btoa(id)
    navigate(`/customer/query/view/${enCodedId}`);
  }

  const handleDateFilter = () => {
    const bodyData = {
      filterType: filters,
      page: 1,
      searchKey: searchValue,
      limit: pageSize,
      columnName: columnId,
      sortingOrder: sortingOrderStatus,
    }
    setCurrentPage(1)
    getQuery(bodyData);
  }

  const handleDateChange = (selectedDates) => {
    if (selectedDates.length === 2) {
      setFilters({
        ...filters,
        startDate: selectedDates[0],
        endDate: selectedDates[1],
      })
    }
  };

  const customeFilterSection = (
    <div className="d-flex align-items-center justify-content-start">
      <InputGroup style={{ minWidth: '250px' }}>
        <Flatpickr
          ref={flatpickrRef}
          className="form-control"
          placeholder="Start Date - End Date"
          options={{
            mode: 'range',
            dateFormat: 'd-m-Y'
          }}
          value={filters?.startDate && filters?.endDate ? [filters?.startDate, filters?.endDate] : []}
          onChange={handleDateChange}
        />
        <Button variant="primary"
          onClick={handleDateFilter}
          style={{ backgroundColor: "#344fa0", border: "none" }}>
          Go
        </Button>
      </InputGroup>
    </div>
  )


  const buttonSection = (
    <div className="text-sm-end">
      {
        <Button
          type="button"
          color="success"
          className="btn-rounded mb-2 me-2"
          onClick={() => {
            navigate("/customer/query/add");
          }}
        >
          <i className="mdi mdi-plus me-1" />
          Add Query
        </Button>
      }
    </div>
  )


  return (
      <div className="page-content">
        <Container fluid>
            <Breadcrumbs breadcrumbItem="Past Queries" />
          <Row>
            <Col xs="12">
              <Card>
                <CardBody>
                <TableContainerV1
                  columns={columns}
                  searchHelperText="Search by Description, Status"
                  data={queryList}
                  pagination={pagination}
                  handlePageChange={handlePageChange}
                  customFilterSection={customeFilterSection}
                  customSection={buttonSection}
                  handleSearchFilter={handleSearchFilter}
                  sortOptions={sortingOptions}
                  handleSortColumn={handleSortColumn}
                  handleClearSearch={handleClearSearch}
                  className="custom-header-css"
                />
              </CardBody>
            </Card>
          </Col>
        </Row>
      </Container>
    </div>
  )
}

export default ListQuery
